/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type ReactNode, createContext, forwardRef, useRef } from 'react';
import { useFocusRing, useHover, usePress } from 'react-aria';
import type {
  InputRenderProps
} from 'react-aria-components';
import {
  type ContextValue,
  DEFAULT_SLOT,
  InputContext as InputContextCore,
  Input as InputCore,
  type InputProps as InputCoreProps,
  Provider,
  useContextProps,
  useRenderProps
} from 'react-aria-components';
import { ChevronDownSmallStrokeIcon } from '../../icons/chevron-down-small-stroke/chevron-down-small-stroke.icon';
import { $ } from '../../tokens.stylex';
import type { StyleOrFunction } from '../../types';
import { applyStylesWithRenderProps } from '../../utils/apply-styles-with-render-props/apply-styles-with-render-props.util';
import { resolveComponentStyle } from '../../utils/resolve-component-style/resolve-component-style.util';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { IconContext } from '../icon/icon.component';
import { ProgressIndicatorContext } from '../progress-indicator/progress-indicator.component';
import { TextContext } from '../text/text.component';

interface InputStyleProps {
  /** The size variant of the input. (Default: 48) */
  size?: keyof typeof sizeVariants;
  /** The variant of the input. (Default: outlined) */
  variant?: keyof typeof variantVariants;
  /** Used to add accessories to the input. */
  children?: ((values: {
    isHovered: boolean;
    isFocused: boolean;
    isFocusVisible: boolean;
    isDisabled: boolean;
    isInvalid: boolean;
    defaultChildren: ReactNode | undefined;
  }) => ReactNode);
  style?: StyleOrFunction<InputRenderProps> | {
    base?: StyleOrFunction<InputRenderProps>;
    input?: StyleOrFunction<InputRenderProps>;
  };
}

type InputProps = InputStyleProps & Omit<InputCoreProps, 'style' | 'className' | 'children'>;

const InputStyleContext = createContext<ContextValue<InputStyleProps, HTMLDivElement>>({});

const Input = forwardRef((props: InputProps, ref: ForwardedRef<HTMLDivElement>) => {
  let inputRef = useRef<HTMLInputElement>(null);
  let contextProps: InputCoreProps = {};

  [props, ref] = useContextProps(props, ref, InputStyleContext);
  [contextProps, inputRef] = useContextProps({}, inputRef, InputContextCore);

  const { style, size = 48, variant = 'outlined', children, onScroll, ...coreProps } = props;

  const { hoverProps, isHovered } = useHover(coreProps);
  const { isFocused, isFocusVisible, focusProps } = useFocusRing({
    isTextInput: true,
    autoFocus: coreProps.autoFocus,
    within: true,
  });
  // onKeyDown prevents spaces from being entered into the input field
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { pressProps: { onKeyDown, ...pressProps } } = usePress({
    onPressStart: () => inputRef.current?.focus(),
    isDisabled: coreProps.disabled,
    preventFocusOnPress: true,
  });

  const isInvalid = (!!coreProps['aria-invalid'] && coreProps['aria-invalid'] !== 'false') || !!contextProps['aria-invalid'];
  const isDisabled = coreProps.disabled || contextProps.disabled || false;

  const renderProps = useRenderProps({
    ...coreProps,
    values: {
      isHovered,
      isFocused,
      isFocusVisible,
      isDisabled,
      isInvalid
    },
    defaultChildren: (
      <InputCore
        ref={inputRef}
        onScroll={(event) => {
          if (!isFocused) {
            event.currentTarget.scrollLeft = 0;
          }
          onScroll?.(event);
        }}
        {...coreProps}
        {...applyStylesWithRenderProps<InputRenderProps>((state) => stylex.props(
          inputStyles.base,
          inputSizeVariants[size],
          isHovered && inputStyles.hovered,
          isFocusVisible && inputStyles.focused,
          isDisabled && inputStyles.disabled,
          resolveComponentStyle(style, state, 'input')))}
      />),
    children: (renderProps) => (
      <Provider values={[
        [ProgressIndicatorContext, {
          size: size === 48 ? 20 : 16,
          variant: 'primary',
        }],
        [IconButtonContext, {
          slots: {
            [DEFAULT_SLOT]: {
              variant: 'ghost',
              size: size === 48 ? 32 : 20,
              style: iconButtonSizeVariants[size],
              isDisabled,
            },
            [INPUT_SLOTS.POPOVER_ICON_BUTTON]: {
              'aria-label': 'Show suggestions',
              variant: 'ghost',
              size: size === 48 ? 32 : 20,
              style: [popoverIconButtonStyles.base, iconButtonSizeVariants[size]],
              isDisabled,
              children: (iconButtonRenderProps) => (
                <ChevronDownSmallStrokeIcon
                  size={20}
                  color={(
                    (renderProps.isDisabled && 'disabled') ||
                    ((renderProps.isHovered || renderProps.isFocused) && 'interactive') ||
                    'quaternary'
                  )}
                  style={[iconStyles.base, iconButtonRenderProps.isPressed && iconStyles.rotated]}
                />
              )
            }
          }
        }],
        [TextContext, {
          slot: null!,
          size: size === 48 ? 16 : 14,
          color: renderProps.isHovered || renderProps.isFocused ? 'placeholder' : 'disabled'
        }],
        [IconContext, {
          color: (isDisabled && 'disabled') || 'tertiary'
        }]]}>
        {children?.(renderProps) || renderProps.defaultChildren}
      </Provider>
    )
  });

  return (
    <div
      ref={ref}
      data-focused={isFocused || undefined}
      data-disabled={isDisabled || undefined}
      data-hovered={isHovered || undefined}
      data-focus-visible={isFocusVisible || undefined}
      data-invalid={isInvalid || undefined}
      {...pressProps}
      {...hoverProps}
      {...focusProps}
      {...renderProps}
      {...stylex.props(styles.base, sizeVariants[size], variantVariants[variant], resolveComponentStyle(style, {
        isDisabled: !!coreProps.disabled,
        isHovered,
        isFocused,
        isFocusVisible,
        isInvalid,
      }, 'base'))}>
      {renderProps.children}
    </div>
  );
});

const styles = stylex.create({
  base: {
    borderStyle: 'solid',
    overflow: 'hidden',
    alignItems: 'center',
    boxSizing: 'border-box',
    columnGap: 12,
    cursor: {
      default: 'text',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'flex',
  },
});

const inputStyles = stylex.create({
  base: {
    borderStyle: 'none',
    flex: '1 1 0',
    outline: 'none',
    overflow: 'hidden',
    alignItems: 'center',
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    caretColor: $.textInteractive,
    color: {
      default: $.textPrimary,
      ':is([data-disabled])': $.textDisabled,
    },
    cursor: 'inherit',
    display: 'flex',
    fontFamily: 'Inter',
    fontFeatureSettings: 'liga off, clig off',
    fontStyle: 'normal',
    fontWeight: 400,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    height: '100%',
    minWidth: 0,
    width: '100%',
    '::placeholder': { color: $.textPlaceholder },
    '::selection': { backgroundColor: $.surfaceHighlightMedium },
    ':-webkit-autofill': {
      WebkitTextFillColor: $.textPrimary,
      boxShadow: `0 0 0 1000px ${$.surfaceBGPrimary} inset`,
      transition: 'background-color 5000s ease-in-out 0s',
    },
    ':-webkit-autofill:hover': {
      WebkitTextFillColor: $.textPrimary,
      boxShadow: `0 0 0 1000px ${$.surfaceBGPrimary} inset`,
    },
    ':-webkit-autofill:focus': {
      WebkitTextFillColor: $.textPrimary,
      boxShadow: `0 0 0 1000px ${$.surfaceBGPrimary} inset`,
    },
    ':-webkit-autofill:active': {
      WebkitTextFillColor: $.textPrimary,
      boxShadow: `0 0 0 1000px ${$.surfaceBGPrimary} inset`,
    },
  },
  hovered: { '::placeholder': { color: $.textQuaternary } },
  disabled: { '::placeholder': { color: $.textDisabled } },
  focused: { '::placeholder': { color: $.textPlaceholder } },
});

const variantVariants = stylex.create({
  outlined: {
    borderColor: {
      default: $.border16,
      ':is([data-disabled])': $.border10,
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.borderInteractive,
      ':is([data-hovered]):not([data-focused], [data-focus-within], [data-invalid], [data-disabled])': $.border24,
      ':is([data-invalid])': $.borderCritical,
    },
    borderWidth: {
      default: 1,
      ':is([data-disabled])': 1,
      ':is([data-focused], [data-focus-within], [data-invalid])': 2,
    },
    backgroundColor: $.surfaceBGPrimary,
  },
  filled: {
    borderColor: {
      default: 'transparent',
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.borderInteractive,
    },
    borderWidth: {
      default: 1,
      ':is([data-disabled])': 1,
      ':is([data-focused], [data-focus-within], [data-invalid])': 2,
    },
    backgroundColor: {
      default: $.opacity06,
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.surfaceBGPrimary,
      ':is([data-invalid])': $.surfaceCriticalSilent,
    },
  },
  underlined: {
    borderRadius: 0,
    backgroundColor: 'transparent',
    borderBottomColor: {
      default: $.border08,
      ':is([data-disabled])': $.border06,
      ':is([data-hovered], [data-focused], [data-focus-within]):not([data-invalid], [data-disabled])': $.borderInteractive,
      ':is([data-invalid])': $.borderCritical,
    },
    borderBottomWidth: 1.5,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderTopWidth: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
});

const sizeVariants = stylex.create({
  48: {
    borderRadius: 10,
    height: 48,
    paddingBottom: {
      default: 5,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 4,
    },
    paddingLeft: {
      default: 14,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 13,
    },
    paddingRight: {
      default: 14,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 13,
    },
    paddingTop: {
      default: 5,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 4,
    },
  },
  32: {
    borderRadius: 8,
    height: 32,
    paddingLeft: {
      default: 12,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 11,
    },
    paddingRight: {
      default: 12,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 11,
    },
  },
});

const inputSizeVariants = stylex.create({
  48: {
    fontSize: 16,
    letterSpacing: -0.5,
  },
  32: {
    fontSize: 14,
    letterSpacing: -0.45,
  },
});

const popoverIconButtonStyles = stylex.create({
  base: {
    backgroundColor: 'transparent'
  }
});

const iconButtonSizeVariants = stylex.create({
  48: {
    marginRight: -4,
  },
  32: {
    marginRight: -6,
  }
});

const iconStyles = stylex.create({
  base: {
    transition: '0.15s',
  },
  rotated: {
    transform: 'rotate(180deg)',
    transformOrigin: 'center center',
  }
});

const INPUT_SLOTS = {
  POPOVER_ICON_BUTTON: 'popover-icon-button'
};

export { INPUT_SLOTS, Input, InputStyleContext, type InputProps, type InputStyleProps };

